import {InsuranceDataModel} from './../../models/insurance-data.model';
import {AgreementModel} from './../../models/agreement.model';
import {EventEmitter, Injectable} from '@angular/core';
import {
  CalculationData,
  CalculationIntegrationParameter,
  CalculationIntegrationParameterName
} from '../../models/calculation-data.model';
import {InsurerData} from '../../models/insurer-data.model';
import {CalculationStorageService} from '../../../shared/services/calculation-storage.service';
import {VehicleDataModel} from '../../models/vehicle-data.model';
import {OwnerDataModel} from '../../models/owner-data/owner-data.model';
import {ContactDataModel} from '../../models/contact-data.model';
import {VehicleStateModel} from '../../models/vehicle-state.model';
import {BehaviorSubject} from 'rxjs/Rx';
import {SessionStorage} from '../global/storage/session-storage.service';
import {ApplicationStateService} from '../../../shared/services/application-state.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {VehicleQuestionsService} from '../vehicle-questions.service';
import {VehicleDataEnum} from '../../consts/vehicle-data.enum';
import {VehicleStateEnum} from '../../consts/vehicle-state.enum';
import {UrlParamsService} from './url-params.service';
import {BusinessParamName, BusinessParamValue} from '../../../shared/models/request-offer/request-offer.enum';
import {SalesChannel} from '../../consts/sales-channel.enum';
import {isNullOrUndefined} from 'util';
import {AdditionalDriverModel} from '../../models/owner-data/additional-driver.model';
import * as moment from 'moment';
import {OlxValuesMappingService} from './olx-values-mapping.service';
import {TextTransformService} from '../../../shared/services/text-transform.service';
import {PeselService} from '../../../shared/services/pesel.service';
import {tomorrow} from '../../../shared/utils/time-utils';
import {QueryParamName} from '../../enums/query-param-name.enum';
import {RegExpKey, RegExpService} from '../../../shared/services/reg-exp.service';

@Injectable()
export class CalculationService {

  calculationData: CalculationData;

  validationEnabled = true;
  dataFromSession: EventEmitter<any> = new EventEmitter();
  calculationDataReady: BehaviorSubject<any> = new BehaviorSubject(null);

  supportedCalculationUrlParams: string[] = [
    QueryParamName.IS_GAS_INSTALLATION,
    QueryParamName.IS_ABROAD,
    QueryParamName.VEHICLE_CONDITION,
    QueryParamName.CONDITION,
    QueryParamName.EMAIL,
    QueryParamName.PHONE_NUMBER,
    QueryParamName.MAKE,
    QueryParamName.BRAND_NAME,
    QueryParamName.FUEL,
    QueryParamName.YEAR,
    QueryParamName.ACTUAL_MILEAGE,
    QueryParamName.VIN,
    QueryParamName.REGISTRATION_NUMBER,
    QueryParamName.FIRST_NAME,
    QueryParamName.LAST_NAME,
    QueryParamName.POSTAL_CODE,
    QueryParamName.PESEL,
  ];

  firstStepCalculationData: any[];
  startInsuranceDateChange$: EventEmitter<void> = new EventEmitter<void>();
  customerAgreementPhotoInit$: EventEmitter<void> = new EventEmitter<void>();

  constructor(private storage: CalculationStorageService,
              private stateService: ApplicationStateService,
              private questionsService: VehicleQuestionsService,
              private urlParamsService: UrlParamsService,
              private olxValueMappingService: OlxValuesMappingService,
              private sessionStorage: SessionStorage,
              private regExpService: RegExpService) {
    this.createOrSetCalculation();
  }

  get hasCoOwners(): boolean {
    return this.ownerData.coOwners && !!this.ownerData.coOwners.length;
  }

  get insurerData(): InsurerData {
    return this.calculationData.insurer;
  }

  get vehicleData(): VehicleDataModel {
    return this.calculationData.vehicle;
  }

  get ownerData(): OwnerDataModel {
    return this.calculationData.owner;
  }

  get contactData(): ContactDataModel {
    return this.calculationData.contact;
  }

  get agreements(): AgreementModel[] {
    return this.calculationData.agreements;
  }

  set agreements(agreements: AgreementModel[]) {
    this.calculationData.agreements = agreements;
  }

  get vehicleState(): VehicleStateModel {
    return this.calculationData.vehicleState;
  }

  get insuranceData(): InsuranceDataModel {
    return this.calculationData.insuranceData;
  }

  get additionalDrivers(): AdditionalDriverModel[] {
    return this.calculationData.additionalDrivers;
  }

  get isVehicleImported(): boolean {
    return this.calculationData.vehicleState.abroadImported !== 'ABROAD_IMPORTED_NO';
  }

  set additionalDrivers(additionalDrivers: AdditionalDriverModel[]) {
    this.calculationData.additionalDrivers = additionalDrivers;
  }

  /**
   * whether part of data (contact data & start insurance date) should be cleared;
   * removes above data params from calculation data for Yanosik in waiting for offer mode (only at first entry to the application)
   */
  get shouldClearPartOfData(): boolean {
    const calculationSource = this.stateService.getBusinessParameterValue(BusinessParamName.CALCULATION_SOURCE);
    return (calculationSource === BusinessParamValue.REMOTE)
      || (this.stateService.salesChannel === SalesChannel.YANOSIK && isNullOrUndefined(calculationSource));
  }

  get urlParams(): any {
    return this.stateService.state.queryParams;
  }

  get isStartInsuranceDateValid(): boolean {
    return isNotNullOrUndefined(this.calculationData.insuranceData.startInsuranceDate) && !moment(this.calculationData.insuranceData.startInsuranceDate).isBefore(tomorrow(), 'day');
  }

  get isRegistrationNumberValid(): boolean {
    const regExp = new RegExp(this.regExpService.getRegExp(RegExpKey.REGISTRATION_NUMBER), 'g');
    return isNotNullOrUndefined(this.vehicleData.registrationNumber) && regExp.test(this.vehicleData.registrationNumber);
  }

  get isBirthDateValid(): boolean {
    return isNotNullOrUndefined(this.ownerData.birthDate);
  }

  createOrSetCalculation(): void {
    this.calculationData = new CalculationData();

    this.dataFromSession.subscribe(calculation => {
      if (calculation) {
        this.calculationData = this.processCalculationDataFromSession(calculation);

        // FIXME proces FTP oraz dekododwanie azteca zwraca inny typ (number) i spacje po niektorych polach
        this.transformationCalculationData(calculation);
        this.stateService.updateCoOwnersState(this.calculationData.owner.coOwners);

        this.storage.createCalculation(this.calculationData);
      } else {
        if (!this.storage.isCalculation()) {
          this.storage.createCalculation(this.calculationData);
          this._initRawCalculation();
        } else {
          this.initCalculationFromStorage();
          this.storage.setCalculationData(this.calculationData);
        }
      }

      if (this.stateService.firstEntry) {
        this.updateCalculationDataWithUrlParams();
      }

      this.initFirstStepCalculationValues();

      this.calculationDataReady.next(this.calculationData);
    });
  }

  public createCalculationDataAndState(): void {
    this.sessionStorage.clear();
    this._initRawCalculation();
    this.storage.updateCalculation();
  }

  resetFirstStepCalculationData(): void {
      this.vehicleData.registrationNumber = null;
      this.vehicleData.productionYear = null;
      this.vehicleData.brandCode = null;
      this.vehicleData.modelCode = null;
      this.vehicleData.engineCapacity = null;
      this.vehicleData.fuelTypeCode = null;
      this.vehicleData.vehicleCodeEurotax = null;
      this.vehicleData.vehicleCodeInfoekspert = null;
      this.vehicleData.vehicleCodeInfoExpertLink4 = null;
      this.vehicleData.vin = null;
      this.insuranceData.startInsuranceDate = null;
      this.storage.updateCalculation();
  }

  markUrlCalculationParamInsterted(): void {
    this.stateService.state.urlCalculationParamInserted = true;
    this.stateService.updateApplicationState();
  }

  setCustomerAgreementPhoto(data: string): void {
    if (!this.hasIntegrationParameter(CalculationIntegrationParameterName.CUSTOMER_AGREEMENT_PHOTO)) {
      this.customerAgreementPhotoInit$.emit();
    }
    this.addIntegrationParameter({
      parameterName: CalculationIntegrationParameterName.CUSTOMER_AGREEMENT_PHOTO,
      parameterValue: data
    });
  }

  getIntegrationParameterValue(name: CalculationIntegrationParameterName): any {
    if (!!this.calculationData.integrationParameters) {
      const param = this.calculationData.integrationParameters.find(parameter => parameter.parameterName === name);
      return param ? param.parameterValue : null;
    }
    return null;
  }

  private _initRawCalculation(): void {
    // calculation data
    this.calculationData.insurer = new InsurerData();
    this.calculationData.owner = new OwnerDataModel();
    this.calculationData.contact = new ContactDataModel();
    this.calculationData.vehicle = new VehicleDataModel();
    this.calculationData.vehicleState = new VehicleStateModel();
    this.calculationData.insuranceData = new InsuranceDataModel();
    this.calculationData.agreements = [];
    this.calculationData.additionalDrivers = [];
    this.calculationData.discounts = {};
  }

  private initFirstStepCalculationValues(): void {
    this.firstStepCalculationData = [
      this.vehicleData.registrationNumber,
      this.vehicleData.productionYear,
      this.vehicleData.brandCode,
      this.vehicleData.modelCode,
      this.vehicleData.engineCapacity,
      this.vehicleData.fuelTypeCode,
      this.vehicleData.vehicleCodeEurotax,
      this.insuranceData.startInsuranceDate
    ];
  }

  private initCalculationFromStorage(): void {
    this.calculationData = this.storage.getCalculationData();
  }

  private processCalculationDataFromSession(data: CalculationData): CalculationData {
    if (!data.hasOwnProperty('owner')) {
      data.owner = new OwnerDataModel();
    }
    if (!data.owner.hasOwnProperty('coOwners')) {
      data.owner.coOwners = [];
    }
    if (!data.hasOwnProperty('insurer')) {
      data.insurer = new InsurerData();
    }
    if (!data.hasOwnProperty('contact')) {
      data.contact = new ContactDataModel();
    }
    if (!data.hasOwnProperty('vehicle')) {
      data.vehicle = new VehicleDataModel();
    }
    if (!isNotNullOrUndefined(data.vehicle.questions)) {
      data.vehicle.questions = [];
    }
    if (!data.hasOwnProperty('vehicleState')) {
      data.vehicleState = new VehicleStateModel();
    }
    if (!isNotNullOrUndefined(data.vehicleState.questions)) {
      data.vehicleState.questions = [];
    }
    if (!data.hasOwnProperty('additionalDrivers')) {
      data.additionalDrivers = [];
    }
    if (!data.hasOwnProperty('insuranceData')) {
      data.insuranceData = new InsuranceDataModel();
    }
    if (this.shouldClearPartOfData) {
      data.contact = new ContactDataModel();
      data.insuranceData.startInsuranceDate = null;
      data.owner.matrimonialStatus = null;
    }

    return data;
  }

  private updateCalculationDataWithUrlParams(): void {
    Object.keys(this.urlParams).forEach(paramName => {
      if (this.supportedCalculationUrlParams.includes(paramName)) {
        this.updateValueFromUrl(paramName);
      }
    });
  }

  private updateValueFromUrl(paramName: any): void {
    const value = this.urlParams[paramName];

    switch (paramName) {
      case QueryParamName.IS_GAS_INSTALLATION: {
        if (this.urlParamsService.validateIsGasInstallation(value)) {
          this.questionsService.updateQuestion(this.calculationData.vehicle, VehicleDataEnum.IS_GAS_INSTALLATION, value);
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      case QueryParamName.IS_ABROAD: {
        if (this.urlParamsService.validateIsAbroad(value)) {
          this.questionsService.updateQuestion(this.calculationData.vehicleState, VehicleStateEnum.IS_ABROAD, value);
          this.stateService.updateDirtyField('VEHICLE_DATA_SECTION', VehicleStateEnum.IS_ABROAD, true);
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      case QueryParamName.VEHICLE_CONDITION: {
        if (this.urlParamsService.validateVehicleCondition(value)) {
          this.calculationData.vehicleState.vehicleCondition = value;
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      case QueryParamName.CONDITION: {
        const vehicleCondition = this.olxValueMappingService.mapVehicleCondition(value);
        if (vehicleCondition) {
          this.calculationData.vehicleState.vehicleCondition = vehicleCondition;
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      // case QueryParamName.YOUNGEST_DRIVER_AGE: {
      //   if (this.urlParamsService.validateYoungestDriverAge(value)) {
      //     this.calculationData.insuranceData.youngestDriverAge = parseInt(value);
      //     this.stateService.updateDirtyField('VEHICLE_DATA_SECTION', InsuranceDataEnum.YOUNGEST_DRIVER_AGE, true);
      //   }
      //   break;
      // }
      case QueryParamName.EMAIL: {
        if (this.urlParamsService.validateEmail(value)) {
          this.calculationData.contact.email = value;
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      case QueryParamName.PHONE_NUMBER: {
        const phoneNumber = value.trim();
        if (this.urlParamsService.validatePhoneNumber(phoneNumber)) {
          this.calculationData.contact.phoneNumber = phoneNumber;
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      case QueryParamName.ACTUAL_MILEAGE: {
        if (this.urlParamsService.validateActualMileage(value)) {
          this.calculationData.vehicleState.actualMileage = parseInt(value);
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      case QueryParamName.VIN: {
        if (this.urlParamsService.validateVin(value)) {
          this.calculationData.vehicle.vin = value;
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      case QueryParamName.REGISTRATION_NUMBER: {
        const registrationNumber = value.toUpperCase();
        if (this.urlParamsService.validateRegistrationNumber(registrationNumber)) {
          this.calculationData.vehicle.registrationNumber = registrationNumber;
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      case QueryParamName.FIRST_NAME: {
        const firstName = TextTransformService.capitalize(value);
        if (this.urlParamsService.validateName(firstName)) {
          this.calculationData.owner.firstName = firstName;
          this.calculationData.insurer.firstName = firstName;
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      case QueryParamName.LAST_NAME: {
        const lastName = TextTransformService.capitalize(value);
        if (this.urlParamsService.validateName(lastName)) {
          this.calculationData.owner.lastName = lastName;
          this.calculationData.insurer.lastName = lastName;
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      case QueryParamName.POSTAL_CODE: {
        if (this.urlParamsService.validatePostalCode(value)) {
          this.calculationData.owner.address.postalCode = value;
          this.calculationData.insurer.address.postalCode = value;
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
      case QueryParamName.PESEL: {
        if (this.urlParamsService.validatePesel(value)) {
          this.calculationData.owner.pesel = value;
          this.calculationData.insurer.pesel = value;

          const birthDate = PeselService.getBirthdate(value);
          this.calculationData.owner.birthDate = birthDate;
          this.calculationData.insurer.birthDate = birthDate;
          this.markUrlCalculationParamInsterted();
        }
        break;
      }
    }
  }

  private transformationCalculationData(calculation) {
    try {
      this.prepareVehicleData(calculation);
      this.prepareContactData(calculation);
      this.prepareInsurerData(calculation);
    } catch (e) {
      console.error('Error in transformation calculation data!', e);
    }
  }

  private prepareInsurerData(calculation) {
    if (isNotNullOrUndefined(calculation.insurer)) {
      if (calculation.insurer.pesel) {
        calculation.insurer.pesel = String(calculation.insurer.pesel).trim();
      }
    }
  }

  private prepareContactData(calculation) {
    if (isNotNullOrUndefined(calculation.contact)) {
      if (calculation.contact.phoneNumber) {
        calculation.contact.phoneNumber = String(calculation.contact.phoneNumber).trim();
      }
      if (calculation.owner.pesel) {
        calculation.owner.pesel = String(calculation.owner.pesel).trim();
      }
    }
  }

  private prepareVehicleData(calculation) {
    if (isNotNullOrUndefined(calculation.vehicle)) {
      if (calculation.vehicle.brandCode) {
        calculation.vehicle.brandCode = String(calculation.vehicle.brandCode).trim();
      }
      if (calculation.vehicle.modelCode) {
        calculation.vehicle.modelCode = String(calculation.vehicle.modelCode).trim();
      }
      if (calculation.vehicle.vehicleCodeEurotax) {
        calculation.vehicle.vehicleCodeEurotax = String(calculation.vehicle.vehicleCodeEurotax).trim();
      }
      if (calculation.vehicle.vehicleCodeInfoekspert) {
        calculation.vehicle.vehicleCodeInfoekspert = String(calculation.vehicle.vehicleCodeInfoekspert).trim();
      }
    }
  }

  private addIntegrationParameter(parameter: CalculationIntegrationParameter): void {
    if (!isNotNullOrUndefined(this.calculationData.integrationParameters)) {
      this.calculationData.integrationParameters = [];
    }
    this.calculationData.integrationParameters.push(parameter);
    this.storage.updateCalculation();
  }

  private hasIntegrationParameter(name: CalculationIntegrationParameterName): boolean {
    if (!!this.calculationData.integrationParameters) {
      return !!this.calculationData.integrationParameters.find(param => param.parameterName === name);
    }
    return false;
  }
}
